import React from 'react';
import SEOMetadata from '../components/SEOMetadata';

export const Terms = () => {
  // Generate SEO metadata for the terms page
  const generateSEOMetadata = () => {
    let baseUrl = window.location.origin;
    if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
      baseUrl = 'https://www.thetreasureroom.com.au';
    }
    
    return {
      title: 'Terms & Conditions - The Treasure Room',
      description: 'Terms and conditions for hiring items from The Treasure Room. Please read these terms carefully before making a hire request.',
      canonicalUrl: `${baseUrl}/terms`,
      ogType: 'website'
    };
  };
  
  const seoMetadata = generateSEOMetadata();

  return (
    <div className="min-h-screen text-white pt-20 relative overflow-hidden">
      <SEOMetadata {...seoMetadata} />
      <div className="absolute inset-0 w-full h-full bg-[#E3C9A8] -z-10" />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="space-y-8">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-4xl font-bold text-[#5E0A0A]">Terms & Conditions of Hire</h1>
              <p className="text-[#5E0A0A] mt-2">
                Please read these terms carefully before making a hire request
              </p>
            </div>
          </div>
          
          <div className="prose prose-lg max-w-none">
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-8 text-[#5E0A0A] space-y-8">
  <p className="text-lg">
    By paying a deposit for hire items, you agree to all of the terms and conditions outlined below. Please read them carefully before making a payment.
  </p>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Definitions</h2>
    <ul className="list-disc pl-6 space-y-2">
      <li><strong>The Hirer</strong>: The Treasure Room</li>
      <li><strong>The Client</strong>: The person(s) hiring products from The Hirer for a fee</li>
      <li><strong>Products</strong>: All items hired by the Client from The Hirer</li>
    </ul>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Bond</h2>
    <p>
      A bond is required for all hires, held until the safe return or pickup of all products in the same condition as hired, minus reasonable wear and tear. The bond will be refunded to the Client, less any fees for breakage, damage, or missing items. The bond amount is a minimum of $50 or 20% of the total hire cost, whichever is greater.
    </p>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Payment</h2>
    <p>
      To secure your booking date for event styling or product hire, a 50% deposit is required. The balance for event styling is due 2 weeks prior to the event date, and for party hire products, it is due at least 3 days prior to the event. We accept cash or bank transfer.
    </p>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Cancellations</h2>
    <p>
      Cancellations must be submitted in writing via email or post. The date of receipt determines the cancellation terms:
    </p>
    <ul className="list-disc pl-6 space-y-2">
      <li>4+ weeks before the agreed date: Full refund of any payments made.</li>
      <li>2–4 weeks before: The 50% deposit is forfeited.</li>
      <li>1 week or less before: The full hire amount is forfeited.</li>
      <li>Any other cancellations are at the discretion of The Treasure Room.</li>
    </ul>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Insurance</h2>
    <p>
      Once products are delivered or picked up, the Client assumes full responsibility and liability until they are returned to The Treasure Room. Clients are responsible for maintaining appropriate insurance policies (liability, property, and casualty) to protect The Treasure Room and its products against claims, loss, or damage. The Treasure Room is not liable for injuries caused by hired products during the hire period.
    </p>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Loss or Damage</h2>
    <p>
      All products remain the property of The Treasure Room. Products are provided for reasonable use within their advertised purpose. If products are lost, stolen, damaged, or not returned, the Client must notify The Treasure Room immediately and is responsible for the cost of replacement or repair, based on current market pricing and availability. If no equivalent product is available, the Client will reimburse The Treasure Room at a cost determined by us. Payments for loss or damage are due within 48 hours of the return due date. Late returns may incur additional fees (see below).
    </p>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Cleaning</h2>
    <p>
      Clients should not clean hired products. Any damage caused by cleaning will be treated as per the Loss or Damage terms. If products are disfigured during the hire period, The Treasure Room will assess and charge a damage fee.
    </p>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Photos</h2>
    <p>
      By hiring our products, you consent to The Treasure Room using any photos provided to us for advertising or artistic purposes, whether for profit or not.
    </p>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Hire Period</h2>
    <p>
      The hire period begins when products are picked up or delivered and ends when they are returned or collected:
    </p>
    <ul className="list-disc pl-6 space-y-2">
      <li><strong>Client Pickup/Drop-off</strong>: By appointment at The Treasure Room's location on an agreed date and time.</li>
      <li><strong>Delivery/Pickup by The Treasure Room</strong>: At the agreed location, date, and time.</li>
    </ul>
    <p>
      Failure to return products within the agreed timeframe will incur a late fee (see below).
    </p>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Late Fee/Extension Fee</h2>
    <p>
      A late fee of 10% of the hire cost per day will be charged for products not returned on time. For example, a $100 hire cost incurs a $10 daily late fee. This will be deducted from the bond.
    </p>
  </section>

  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#5E0A0A] pt-4">Hire Rates</h2>
    <p>
      Hire rates cover product hire only and do not include additional services. Any setup or installation services, if agreed, will be outlined separately in writing by The Treasure Room. We do not provide product setup unless specified.
    </p>
  </section>

  <footer className="pt-8 mt-8 border-t border-[#5E0A0A]/20">
    <p className="text-sm text-[#5E0A0A]/80">© 2025 The Treasure Room. All Rights Reserved.</p>
  </footer>
</div>
          </div>
        </div>
      </div>
    </div>
  );
}; 